import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BrainCircuit, 
  Calendar, 
  TrendingUp, 
  BarChart3, 
  Target, 
  Users,
  ArrowRight,
  Play,
  Check
} from 'lucide-react';

const features = [
  {
    icon: BrainCircuit,
    title: 'IA Avançada',
    description: 'Algoritmos inteligentes que analisam tendências e geram ideias personalizadas para o seu conteúdo.'
  },
  {
    icon: Calendar,
    title: 'Planejamento Estratégico',
    description: 'Calendário de conteúdo otimizado com base nos melhores horários para seu público.'
  },
  {
    icon: TrendingUp,
    title: 'Análise de Tendências',
    description: 'Identificação em tempo real de tópicos em alta no seu nicho.'
  },
  {
    icon: BarChart3,
    title: 'Métricas Detalhadas',
    description: 'Acompanhe o desempenho do seu conteúdo com análises profundas.'
  },
  {
    icon: Target,
    title: 'Segmentação Precisa',
    description: 'Alcance o público certo com recomendações baseadas em dados.'
  },
  {
    icon: Users,
    title: 'Engajamento Otimizado',
    description: 'Aumente suas interações com estratégias testadas e aprovadas.'
  }
];

export default function ResourcesPage() {
  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-indigo-50 to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Ferramentas Poderosas para Impulsionar Seu Crescimento
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Descubra como a inteligência artificial da NovaContent transforma suas ideias em estratégias de conteúdo impactantes.
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="p-8 rounded-2xl border border-gray-100 hover:border-indigo-100 bg-white hover:shadow-lg transition-all group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center mb-6 group-hover:bg-indigo-600 transition-colors">
                  <Icon className="h-6 w-6 text-indigo-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Interactive Demo Section */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Veja a Kontty em Ação
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Nossa plataforma combina inteligência artificial com análise de dados para criar estratégias de conteúdo que realmente funcionam.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="ml-4 text-gray-600">
                      Aumente seu engajamento em até 300% com conteúdo otimizado por IA
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="ml-4 text-gray-600">
                      Economize até 15 horas por semana no planejamento de conteúdo
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="ml-4 text-gray-600">
                      Acesse insights exclusivos sobre seu mercado e concorrência
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80"
                    alt="Dashboard NovaContent"
                    className="w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  <button className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-4 rounded-full shadow-lg group">
                    <Play className="h-8 w-8 text-indigo-600 group-hover:scale-110 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Pronto para Transformar seu Conteúdo?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Junte-se a milhares de criadores que já estão usando a NovaContent para crescer nas redes sociais.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/cadastro"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors group"
            >
              Comece Agora
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-indigo-600 bg-white border-2 border-indigo-600 rounded-full hover:bg-indigo-50 transition-colors"
            >
              Agendar Demo
              <Play className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}